// project imports
import { CustomTypography } from 'src/themes/types';
import { adminTokens } from 'src/views/common/adminTokens';
import { getIconSvgPath } from 'src/utils/image';

const alertIcon = (iconName: string) => {
    const src = getIconSvgPath(iconName);
    const mask = `url(${src}) no-repeat center / contain`;
    return {
        '& .MuiAlert-icon > svg': { display: 'none' },
        '& .MuiAlert-icon::after': {
            content: '""',
            display: 'block',
            width: 20,
            height: 20,
            backgroundColor: 'currentColor',
            mask,
            WebkitMask: mask,
        },
    };
};

const clockMeridiemPill = {
    width: 'auto',
    height: 28,
    minWidth: 44,
    padding: '0 12px',
    borderRadius: adminTokens.radiusFull,
    fontFamily: adminTokens.fontSans,
    fontSize: `${adminTokens.textSm}px`,
    fontWeight: 600,
    color: adminTokens.gray600,
    backgroundColor: adminTokens.surface,
    border: `1px solid ${adminTokens.gray300}`,
    '&:hover': {
        backgroundColor: adminTokens.gray50,
        borderColor: adminTokens.gray400,
    },
    '& .MuiTypography-root': {
        color: 'inherit',
        fontFamily: adminTokens.fontSans,
        fontSize: `${adminTokens.textSm}px`,
        fontWeight: 600,
        lineHeight: 1,
    },
};

const clockMeridiemPillActive = {
    backgroundColor: adminTokens.ink,
    borderColor: adminTokens.ink,
    color: '#FFFFFF',
    '&:hover': {
        backgroundColor: adminTokens.inkHover,
        borderColor: adminTokens.inkHover,
    },
};

export default function componentStyleOverrides(theme: CustomTypography) {
    return {
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: adminTokens.radiusMd,
                    fontSize: `${adminTokens.textBase}px`,
                    textTransform: 'none',
                    fontFamily: adminTokens.fontSans,
                }
            }
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0
            },
            styleOverrides: {
                root: {
                    backgroundImage: 'none'
                },
                rounded: {
                    borderRadius: `${theme?.customization?.borderRadius}px`
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    color: theme.colors?.textDark,
                    padding: '24px'
                },
                title: {
                    fontSize: '1.125rem'
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '24px'
                }
            }
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: '24px'
                }
            }
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    color: theme.darkTextPrimary,
                    margin: "0px 0px 2px",
                    padding: "4px 12px 4px 12px",
                    '&.Mui-selected': {
                        color: adminTokens.navy800,
                        backgroundColor: adminTokens.gray100,
                        '&:hover': {
                            backgroundColor: adminTokens.gray100
                        },
                        '& .MuiListItemIcon-root': {
                            color: theme.menuSelected
                        }
                    },
                    '&:hover': {
                        backgroundColor: theme.menuSelectedBack,
                        color: theme.menuSelected,
                        '& .MuiListItemIcon-root': {
                            color: '#484848',
                        }
                    }
                }
            }
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: theme.darkTextPrimary,
                    minWidth: '36px'
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    color: theme.textDark
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    color: adminTokens.navy800,
                    '&::placeholder': {
                        color: adminTokens.gray400,
                        fontSize: `${adminTokens.textBase}px`,
                        fontWeight: 400,
                        fontFamily: adminTokens.fontSans,
                        opacity: 1,
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    padding: 0,
                    borderRadius: adminTokens.radiusMd,
                    background: adminTokens.surface,
                    fontSize: `${adminTokens.textBase}px`,
                    lineHeight: '20px',
                    fontFamily: adminTokens.fontSans,
                    outline: 'none',
                    '&:not(.MuiInputBase-multiline)': {
                        height: `${adminTokens.controlHlg}px`,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: adminTokens.gray300,
                    },
                    '&.Mui-focused': {
                        outline: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: adminTokens.ink,
                        borderWidth: '1px',
                        boxShadow: `0 0 0 3px ${adminTokens.inkFocus}`,
                    },
                },
                input: {
                    fontSize: `${adminTokens.textBase}px`,
                    fontWeight: 400,
                    padding: '0 12px',
                    color: adminTokens.navy800,
                    fontFamily: adminTokens.fontSans,
                    lineHeight: '20px',
                    outline: 'none',
                    '&:focus': {
                        outline: 'none',
                    },
                    '&:not(.MuiInputBase-inputMultiline)': {
                        height: `${adminTokens.controlHlg}px`,
                        boxSizing: 'border-box' as const,
                    },
                    '&.MuiInputBase-inputMultiline': {
                        padding: '10px 12px',
                    },
                },
                inputAdornedStart: {
                    paddingLeft: 4
                },
                notchedOutline: {
                    borderRadius: adminTokens.radiusMd,
                    borderColor: adminTokens.gray300,
                    borderWidth: '1px',
                }
            }
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        color: theme?.customization?.navType === 'dark' ? theme.colors?.darkTextPrimary! + 50 : theme.colors?.grey300
                    }
                },
                mark: {
                    backgroundColor: theme.paper,
                    width: '4px'
                },
                valueLabel: {
                    color: theme?.customization?.navType === 'dark' ? theme?.colors?.primaryMain : theme?.colors?.primaryLight
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        background: adminTokens.surface,
                        padding: '6px 8px !important',
                        height: 'auto !important' as any,
                        minHeight: '40px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '6px',
                    },
                    '& .MuiAutocomplete-input': {
                        padding: '2px 4px !important',
                        height: 'auto !important' as any,
                        minWidth: '60px',
                    },
                    '& .MuiAutocomplete-tag': {
                        margin: 0,
                        height: 28,
                        border: 'none',
                        borderRadius: adminTokens.radius,
                        background: adminTokens.gray100,
                        color: adminTokens.navy800,
                        fontSize: adminTokens.textSm,
                        fontWeight: 500,
                        '.MuiChip-label': {
                            padding: '0 10px',
                        },
                        '.MuiChip-deleteIcon': {
                            color: adminTokens.gray500,
                            margin: '0 6px 0 -2px',
                            '&:hover': { color: adminTokens.navy800 },
                        }
                    }
                },
                popper: {
                    zIndex: 1400,
                },
                paper: {
                    marginTop: '4px',
                    background: adminTokens.surface,
                    border: `1px solid ${adminTokens.gray200}`,
                    borderRadius: adminTokens.radiusMd,
                    boxShadow: adminTokens.shadowDropdown,
                    overflow: 'hidden',
                },
                listbox: {
                    padding: '4px',
                    maxHeight: '280px',
                },
                option: {
                    minHeight: '36px !important',
                    padding: '0 12px',
                    margin: '2px 0',
                    borderRadius: adminTokens.radius,
                    fontFamily: adminTokens.fontSans,
                    fontSize: `${adminTokens.textBase}px`,
                    fontWeight: 500,
                    color: adminTokens.navy800,
                    '&:hover': { backgroundColor: adminTokens.gray100 },
                    '&.Mui-focused': { backgroundColor: adminTokens.gray100 },
                    '&[aria-selected="true"]': {
                        backgroundColor: adminTokens.gray100,
                        fontWeight: 600,
                    },
                    '&[aria-selected="true"].Mui-focused': {
                        backgroundColor: adminTokens.gray100,
                    },
                },
                noOptions: {
                    fontFamily: adminTokens.fontSans,
                    fontSize: `${adminTokens.textBase}px`,
                    color: adminTokens.gray500,
                    padding: '10px 12px',
                },
                loading: {
                    fontFamily: adminTokens.fontSans,
                    fontSize: `${adminTokens.textBase}px`,
                    color: adminTokens.gray500,
                    padding: '10px 12px',
                },
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: theme.divider,
                    opacity: theme?.customization?.navType === 'dark' ? 0.2 : 1
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    minHeight: '40px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: adminTokens.surface,
                    outline: 'none',
                    '&:focus': {
                        backgroundColor: adminTokens.surface,
                        outline: 'none',
                    }
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 40,
                    height: 22,
                    padding: 0,
                },
                switchBase: {
                    padding: '2px',
                    '&.Mui-checked': {
                        transform: 'translateX(18px)',
                        color: '#ffffff',
                        '& + .MuiSwitch-track': {
                            backgroundColor: adminTokens.ink,
                            opacity: 1,
                        },
                    },
                },
                thumb: {
                    width: 18,
                    height: 18,
                    boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                },
                track: {
                    borderRadius: 999,
                    backgroundColor: adminTokens.gray300,
                    opacity: 1,
                },
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: adminTokens.gray300,
                    padding: '6px',
                    '&.Mui-checked': {
                        color: adminTokens.navy800,
                    },
                    '& .MuiSvgIcon-root': {
                        fontSize: 20,
                        borderRadius: '5px',
                    },
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    color: theme?.customization?.navType === 'dark' ? theme.colors?.darkLevel1 : theme.colors?.primaryDark,
                    background: theme?.customization?.navType === 'dark' ? theme.darkTextPrimary : theme.colors?.primary200
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontFamily: adminTokens.fontSans,
                    fontWeight: 500,
                    '&.MuiChip-deletable .MuiChip-deleteIcon': {
                        color: 'inherit'
                    }
                },
                sizeSmall: {
                    height: 26,
                    fontSize: '13px',
                },
            }
        },
        MuiTimelineContent: {
            styleOverrides: {
                root: {
                    color: theme.textDark,
                    fontSize: '13px'
                }
            }
        },
        MuiTreeItem: {
            styleOverrides: {
                label: {
                    marginTop: 14,
                    marginBottom: 14
                }
            }
        },
        MuiTimelineDot: {
            styleOverrides: {
                root: {
                    boxShadow: 'none'
                }
            }
        },
        MuiInternalDateTimePickerTabs: {
            styleOverrides: {
                tabs: {
                    backgroundColor: theme?.customization?.navType === 'dark' ? theme.colors?.darkPaper : theme.colors?.primaryLight,
                    '& .MuiTabs-flexContainer': {
                        borderColor:
                            theme?.customization?.navType === 'dark' ? theme?.colors?.darkTextPrimary! + 20 : theme.colors?.primary200
                    },
                    '& .MuiTab-root': {
                        color: theme?.customization?.navType === 'dark' ? theme.colors?.darkTextSecondary : theme.colors?.grey900
                    },
                    '& .MuiTabs-indicator': {
                        backgroundColor: theme.colors?.primaryDark
                    },
                    '& .Mui-selected': {
                        color: theme.colors?.primaryDark
                    }
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    borderBottom: '1px solid',
                    borderColor: theme?.customization?.navType === 'dark' ? theme.colors?.darkTextPrimary! + 20 : theme.colors?.grey200
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: adminTokens.radiusXl,
                    boxShadow: '0 12px 40px rgba(12,20,38,.18)',
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: adminTokens.gray200,
                    fontFamily: adminTokens.fontSans,
                    '&:not(.MuiTableCell-head)': {
                        fontSize: '14px',
                        color: adminTokens.navy800,
                        '&.MuiTableCell-root': {
                            padding: '0 16px',
                            height: '56px',
                        }
                    },
                    '&.MuiTableCell-head': {
                        height: '44px',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: adminTokens.gray600,
                        backgroundColor: adminTokens.gray25,
                        padding: '0 16px',
                    }
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    color: theme?.customization?.navType === 'dark' ? theme.colors?.darkLevel1 : theme.paper,
                    background: theme?.customization?.navType === 'dark' ? theme.colors?.grey50 : theme.colors?.grey700
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.25rem'
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    color: adminTokens.errorSolid,
                    fontWeight: 500,
                    fontFamily: 'Inter',
                    paddingTop: '3px',
                    margin: 0
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    padding: '0px 6px',
                    '& .MuiIconButton-root': {
                        width: 28,
                        height: 28,
                        padding: '4px',
                        marginRight: 0,
                        borderRadius: adminTokens.radiusMd,
                        color: adminTokens.gray500,
                        '&:hover': {
                            backgroundColor: adminTokens.gray100,
                            color: adminTokens.navy800,
                        },
                        '&:focus-visible': {
                            outline: 'none',
                            boxShadow: `0 0 0 3px ${adminTokens.inkFocus}`,
                        },
                    },
                    '& .MuiIconButton-root .MuiSvgIcon-root': {
                        fontSize: 18,
                    },
                },
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    padding: '0 12px',
                    margin: '2px 4px',
                    height: 36,
                    borderRadius: adminTokens.radius,
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: adminTokens.fontSans,
                    color: adminTokens.navy800,
                    '&:hover': {
                        backgroundColor: adminTokens.gray100,
                    },
                    '&.Mui-selected': {
                        backgroundColor: adminTokens.gray100,
                        fontWeight: 600,
                        color: adminTokens.navy800,
                        '&:hover': {
                            backgroundColor: adminTokens.gray100,
                        },
                    },
                    '&.Mui-focusVisible': {
                        boxShadow: `0 0 0 3px ${adminTokens.inkFocus}`,
                    },
                }
            }
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    border: `1px solid ${adminTokens.gray200}`,
                    borderRadius: adminTokens.radiusMd,
                    boxShadow: '0 4px 16px rgba(12,20,38,.10)',
                    padding: '4px',
                },
            },
        },
        MuiPickersPopper: {
            styleOverrides: {
                paper: {
                    border: `1px solid ${adminTokens.gray200}`,
                    borderRadius: adminTokens.radiusLg,
                    boxShadow: adminTokens.shadowDropdown,
                    fontFamily: adminTokens.fontSans,
                    overflow: 'hidden',
                    '& .MuiDialogActions-root': {
                        padding: '8px 12px',
                        borderTop: '1px solid ' + adminTokens.gray200,
                        backgroundColor: adminTokens.gray25,
                    },
                },
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: {
                    paddingLeft: '16px',
                    paddingRight: '8px',
                    marginTop: '12px',
                },
                label: {
                    fontFamily: adminTokens.fontSans,
                    fontSize: `${adminTokens.textBase}px`,
                    fontWeight: 600,
                    color: adminTokens.navy800,
                },
                switchViewButton: {
                    color: adminTokens.gray500,
                    borderRadius: adminTokens.radiusMd,
                    '&:hover': { backgroundColor: adminTokens.gray100, color: adminTokens.navy800 },
                },
            },
        },
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                root: {
                    alignItems: 'center',
                },
                button: {
                    width: 28,
                    height: 28,
                    padding: '4px',
                    color: adminTokens.gray500,
                    borderRadius: adminTokens.radiusMd,
                    '&:hover': {
                        backgroundColor: adminTokens.gray100,
                        color: adminTokens.navy800,
                    },
                    '&.Mui-disabled': {
                        color: adminTokens.gray300,
                    },
                    '& .MuiSvgIcon-root': {
                        fontSize: 18,
                    },
                },
                spacer: {
                    width: 2,
                },
            },
        },
        MuiClockPicker: {
            styleOverrides: {
                root: {
                    position: 'relative',
                },
                arrowSwitcher: {
                    top: 10,
                    right: 10,
                },
            },
        },
        MuiDayPicker: {
            styleOverrides: {
                weekDayLabel: {
                    fontFamily: adminTokens.fontSans,
                    fontSize: `${adminTokens.textSm}px`,
                    fontWeight: 600,
                    color: adminTokens.gray600,
                },
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    fontFamily: adminTokens.fontSans,
                    fontSize: `${adminTokens.textSm}px`,
                    fontWeight: 500,
                    color: adminTokens.navy800,
                    borderRadius: adminTokens.radiusMd,
                    '&:hover': { backgroundColor: adminTokens.gray100 },
                    '&.Mui-disabled': { color: adminTokens.gray400 },
                    '&&.Mui-selected': {
                        backgroundColor: adminTokens.ink,
                        color: '#FFFFFF',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: adminTokens.inkHover, color: '#FFFFFF' },
                        '&:focus': { backgroundColor: adminTokens.ink, color: '#FFFFFF' },
                        '&.Mui-focusVisible': {
                            backgroundColor: adminTokens.ink,
                            boxShadow: `0 0 0 3px ${adminTokens.inkFocus}`,
                        },
                        '&.Mui-disabled': { backgroundColor: adminTokens.ink, color: '#FFFFFF', opacity: 0.55 },
                    },
                },
                today: {
                    '&:not(.Mui-selected)': {
                        borderColor: adminTokens.gray300,
                    },
                },
            },
        },
        MuiDateTimePickerTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTabs-flexContainer': { borderBottom: 'none' },
                    '& .MuiTab-root': { color: adminTokens.gray500 },
                    '& .Mui-selected': { color: adminTokens.navy800 },
                    '& .MuiTabs-indicator': { backgroundColor: adminTokens.navy800, height: 2 },
                },
            },
        },
        MuiClock: {
            styleOverrides: {
                root: {
                    margin: '18px 16px 16px',
                },
                clock: {
                    backgroundColor: adminTokens.gray50,
                    boxShadow: `inset 0 0 0 1px ${adminTokens.gray200}`,
                },
                pin: {
                    backgroundColor: adminTokens.ink,
                },
                amButton: ({ ownerState }: any) => ({
                    ...clockMeridiemPill,
                    ...(ownerState && ownerState.meridiemMode === 'am' ? clockMeridiemPillActive : null),
                }),
                pmButton: ({ ownerState }: any) => ({
                    ...clockMeridiemPill,
                    ...(ownerState && ownerState.meridiemMode === 'pm' ? clockMeridiemPillActive : null),
                }),
            },
        },
        MuiClockNumber: {
            styleOverrides: {
                root: {
                    fontFamily: adminTokens.fontSans,
                    fontSize: `${adminTokens.textBase}px`,
                    fontWeight: 500,
                    color: adminTokens.navy800,
                    '&.Mui-selected': { color: '#FFFFFF', fontWeight: 600 },
                    '&.Mui-disabled': { color: adminTokens.gray400 },
                },
            },
        },
        MuiPickersToolbar: {
            styleOverrides: {
                root: {
                    fontFamily: adminTokens.fontSans,
                    color: adminTokens.navy800,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    alignItems: 'flex-start',
                    borderRadius: adminTokens.radiusMd,
                    fontFamily: adminTokens.fontSans,
                    fontSize: '14px',
                    padding: '16px',
                },
                icon: {
                    padding: 0,
                    marginRight: '12px',
                    alignItems: 'center',
                    height: '21px',
                },
                message: {
                    padding: 0,
                },
                standardInfo: {
                    backgroundColor: adminTokens.infoBg,
                    color: adminTokens.infoText,
                    border: `1px solid ${adminTokens.infoBorder}`,
                    '& .MuiAlert-icon': { color: adminTokens.infoText },
                    ...alertIcon('info'),
                },
                standardSuccess: {
                    backgroundColor: adminTokens.successBg,
                    color: adminTokens.successText,
                    border: '1px solid #BFE3CD',
                    '& .MuiAlert-icon': { color: adminTokens.green600 },
                    ...alertIcon('check-circle'),
                },
                standardWarning: {
                    backgroundColor: adminTokens.warningBg,
                    color: adminTokens.warningText,
                    border: '1px solid #F4DBA8',
                    '& .MuiAlert-icon': { color: adminTokens.warningSolid },
                    ...alertIcon('warning'),
                },
                standardError: {
                    backgroundColor: adminTokens.errorBg,
                    color: adminTokens.errorText,
                    border: '1px solid #FAC5C1',
                    '& .MuiAlert-icon': { color: adminTokens.errorSolid },
                    ...alertIcon('warning-circle'),
                },
                outlinedInfo: { '& .MuiAlert-icon': { color: adminTokens.infoText }, ...alertIcon('info') },
                outlinedSuccess: { '& .MuiAlert-icon': { color: adminTokens.green600 }, ...alertIcon('check-circle') },
                outlinedWarning: { '& .MuiAlert-icon': { color: adminTokens.warningSolid }, ...alertIcon('warning') },
                outlinedError: { '& .MuiAlert-icon': { color: adminTokens.errorSolid }, ...alertIcon('warning-circle') },
            },
        },
    };
}
